import { Injectable, OnDestroy } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppUser, ProjectRole } from '../models/user.model';
import { Team } from '../models/team.model';
import { TeamService } from './team.service';
import { BackendApiService } from './backend-api.service';
import { ProjectContextService } from './project-context.service';

@Injectable({
  providedIn: 'root'
})
export class UserService implements OnDestroy {
  private defaultUser: AppUser = {
    reference: 'user-owner',
    displayName: '',
    email: '',
    role: 'Owner & Lead Dev',
    projectRole: 'owner'
  };

  private currentUserSubject = new BehaviorSubject<AppUser>(this.defaultUser);
  public currentUser$: Observable<AppUser> = this.currentUserSubject.asObservable();

  private authSub?: Subscription;
  private teamSub?: Subscription;

  public isPlaceholderName(val?: string | null): boolean {
    if (!val) return true;
    const clean = val.trim().toLowerCase();
    return (
      clean === '' ||
      clean === 'sprinta user' ||
      clean === 'sprint user' ||
      clean === 'sprinta' ||
      clean === 'user' ||
      clean === 'sprinta owner' ||
      clean === 'owner'
    );
  }

  constructor(
    private auth: AuthService,
    private teamService: TeamService,
    private backendApi: BackendApiService,
    private projectContext: ProjectContextService
  ) {
    this.authSub = this.auth.user$.subscribe(authUser => {
      if (authUser && (authUser.name || authUser.email)) {
        const email = authUser.email || '';
        const rawName = authUser.name || authUser.nickname || '';
        const name = (!rawName || this.isPlaceholderName(rawName)) ? email : rawName;
        const reference = authUser.sub || email || 'user-auth0';

        const updatedUser: AppUser = {
          reference,
          displayName: name || email,
          email,
          picture: authUser.picture || null,
          role: 'Owner & Lead Dev',
          projectRole: 'owner'
        };

        this.currentUserSubject.next(updatedUser);

        // Fetch backend identity & active project
        this.backendApi.getCurrentUser().subscribe({
          next: (res) => {
            if (res.user) {
              const role = (res.projects && res.projects[0]?.role) || 'owner';
              const backendName = res.user.displayName;
              const finalDisplayName = this.isPlaceholderName(backendName)
                ? (updatedUser.displayName || email)
                : (backendName || email);

              this.currentUserSubject.next({
                ...updatedUser,
                displayName: finalDisplayName,
                picture: res.user.avatarUrl || updatedUser.picture,
                projectRole: role as ProjectRole
              });

              // If backend profile has generic or empty displayName, persist true identity to DynamoDB
              if (this.isPlaceholderName(backendName) && finalDisplayName) {
                this.backendApi.updateProfile({ displayName: finalDisplayName, email }).subscribe({
                  error: (err) => console.warn('UserService: profile sync note', err)
                });
              }
            }
          },
          error: (err) => console.warn('UserService: /me resolution note', err)
        });
      } else if (!authUser) {
        this.currentUserSubject.next(this.defaultUser);
      }
    });

    this.teamSub = this.teamService.currentTeam$.subscribe(team => {
      if (team && team.title) {
        const current = this.currentUserSubject.value;
        if (!current.teamReference) {
          this.currentUserSubject.next({
            ...current,
            teamReference: team.reference
          });
        }
      }
    });
  }

  getCurrentUser(): AppUser {
    const user = this.currentUserSubject.value;
    if (this.isPlaceholderName(user.displayName) && user.email) {
      user.displayName = user.email;
    }
    return user;
  }

  setCurrentUser(user: AppUser): void {
    this.currentUserSubject.next(user);
  }

  switchPersona(persona: AppUser): void {
    this.currentUserSubject.next(persona);
    if (persona.projectRole === 'developer') {
      const devTeam = this.teamService.getUserTeam(persona);
      if (devTeam) {
        this.teamService.setCurrentTeam(devTeam);
      }
    }
  }

  public visibleTeams$: Observable<Team[]> = combineLatest([
    this.currentUser$,
    this.teamService.teams$
  ]).pipe(
    map(([user, teams]) => {
      if (user.projectRole === 'developer') {
        const devTeam = this.teamService.getUserTeam(user);
        if (devTeam) return [devTeam];
        if (user.teamReference) {
          const match = teams.find(t => t.reference === user.teamReference || t.title === user.teamReference);
          if (match) return [match];
        }
        return teams.slice(0, 1);
      }
      if (user.projectRole === 'external_stakeholder') {
        return [];
      }
      return teams;
    })
  );

  getVisibleTeams(): Observable<Team[]> {
    return this.visibleTeams$;
  }

  getPersonas(): AppUser[] {
    return [this.currentUserSubject.value];
  }

  get projectRole(): ProjectRole {
    return this.currentUserSubject.value.projectRole || 'owner';
  }

  isOwner(): boolean {
    return this.projectRole === 'owner';
  }

  isDeveloper(): boolean {
    return this.projectRole === 'developer';
  }

  isInternalStakeholder(): boolean {
    return this.projectRole === 'internal_stakeholder';
  }

  isExternalStakeholder(): boolean {
    return this.projectRole === 'external_stakeholder';
  }

  canViewBacklog(): boolean {
    return this.projectRole !== 'external_stakeholder';
  }

  canViewSprint(): boolean {
    return this.projectRole !== 'external_stakeholder';
  }

  canViewTeams(): boolean {
    return this.projectRole !== 'external_stakeholder';
  }

  canEditRoadmap(): boolean {
    return this.isOwner();
  }

  canEditTeam(teamRefOrTitle?: string): boolean {
    if (this.isOwner()) return true;
    if (!teamRefOrTitle) return false;
    if (this.isDeveloper()) {
      const user = this.getCurrentUser();
      const devTeam = this.teamService.getUserTeam(user);
      if (devTeam && (devTeam.reference === teamRefOrTitle || devTeam.title === teamRefOrTitle)) {
        return true;
      }
      const currentTeam = this.teamService.getCurrentTeam();
      if (currentTeam && (currentTeam.reference === teamRefOrTitle || currentTeam.title === teamRefOrTitle)) {
        return currentTeam.members.some(m => m.status === 'active' && (m.email.toLowerCase() === user.email.toLowerCase() || m.displayName === user.displayName));
      }
      return user.teamReference === teamRefOrTitle;
    }
    return false;
  }

  canEditSprint(teamRefOrTitle?: string): boolean {
    return this.canEditTeam(teamRefOrTitle);
  }

  getRoleLabel(role?: ProjectRole): string {
    switch (role || this.projectRole) {
      case 'owner': return 'Owner';
      case 'developer': return 'Developer';
      case 'internal_stakeholder': return 'Stakeholder (Internal)';
      case 'external_stakeholder': return 'Stakeholder (External)';
      default: return 'Owner';
    }
  }

  getRoleIcon(role?: ProjectRole): string {
    switch (role || this.projectRole) {
      case 'owner': return 'admin_panel_settings';
      case 'developer': return 'code';
      case 'internal_stakeholder': return 'visibility';
      case 'external_stakeholder': return 'public';
      default: return 'person';
    }
  }

  getRoleDetails(role?: ProjectRole): { title: string; description: string; icon: string; color: string } {
    switch (role || this.projectRole) {
      case 'owner':
        return {
          title: 'Owner',
          icon: 'admin_panel_settings',
          color: '#e65100',
          description: 'Full administrative access. Manages projects, subscriptions, teams, roadmap milestones, and member roles.'
        };
      case 'developer':
        return {
          title: 'Developer',
          icon: 'code',
          color: '#1565c0',
          description: 'Team member access. Creates and estimates tasks, collaborates in sprints, and moves tasks across workflow columns.'
        };
      case 'internal_stakeholder':
        return {
          title: 'Stakeholder (Internal)',
          icon: 'visibility',
          color: '#6a1b9a',
          description: 'Internal observer access. Full visibility into project roadmap, sprints, backlog, and team progress in read-only mode.'
        };
      case 'external_stakeholder':
        return {
          title: 'Stakeholder (External)',
          icon: 'public',
          color: '#2e7d32',
          description: 'External stakeholder access. Restricted visibility to allocated roadmap chapters and high-level milestone progress.'
        };
      default:
        return {
          title: 'Owner',
          icon: 'admin_panel_settings',
          color: '#e65100',
          description: 'Full administrative access. Manages projects, subscriptions, teams, roadmap milestones, and member roles.'
        };
    }
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.teamSub?.unsubscribe();
  }
}
